import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;

// Always send stored token as Bearer header
const TOKEN_KEY   = "manna_access_token";
const REFRESH_KEY = "manna_refresh_token";
const getStoredToken   = () => localStorage.getItem(TOKEN_KEY);
const setStoredToken   = (t) => t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY);
const setStoredRefresh = (t) => t ? localStorage.setItem(REFRESH_KEY, t) : localStorage.removeItem(REFRESH_KEY);
const getStoredRefresh = () => localStorage.getItem(REFRESH_KEY);

// Set default auth header for all axios requests
function applyToken(token) {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
}

// On module load, restore token from storage
const existingToken = getStoredToken();
if (existingToken) applyToken(existingToken);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    applyToken(token);
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.kitchens && data.kitchens.length > 0) {
        localStorage.setItem("manna_kitchens_cache", JSON.stringify(data.kitchens));
      }
      setUser(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        const refreshToken = getStoredRefresh();
        if (refreshToken) {
          try {
            const { data } = await axios.post(`${API}/api/auth/refresh`, { refresh_token: refreshToken });
            const newToken = data.access_token;
            setStoredToken(newToken);
            if (data.refresh_token) setStoredRefresh(data.refresh_token);
            applyToken(newToken);
            const me = await axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${newToken}` } });
            setUser(me.data);
            return;
          } catch {
            setStoredToken(null);
            setStoredRefresh(null);
          }
        }
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // ── Auto-refresh permissions ───────────────────────────────────────────────
  // 1. When window regains focus (admin may have updated the role)
  // 2. Every 5 minutes in the background
  useEffect(() => {
    const onFocus = () => {
      if (getStoredToken()) fetchMe();
    };
    window.addEventListener("focus", onFocus);
    const interval = setInterval(() => {
      if (getStoredToken()) fetchMe();
    }, 5 * 60 * 1000); // 5 minutes
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(interval);
    };
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/api/auth/login`, { email, password });

    // ── Forced password change intercept ──────────────────────────────────
    if (data.must_change_password) {
      // Return the flag and reset token without setting a session
      return { must_change_password: true, password_reset_token: data.password_reset_token };
    }

    // Store tokens from response body
    if (data.access_token) {
      setStoredToken(data.access_token);
      applyToken(data.access_token);
    }
    if (data.refresh_token) {
      setStoredRefresh(data.refresh_token);
    }
    if (data.kitchens && data.kitchens.length > 0) {
      localStorage.setItem("manna_kitchens_cache", JSON.stringify(data.kitchens));
    }
    const userState = { ...data };
    delete userState.access_token;
    delete userState.refresh_token;
    setUser(userState);
    return userState;
  };

  // Used by ForcePasswordReset after successful reset to establish session
  const setSession = (data) => {
    if (data.access_token) {
      setStoredToken(data.access_token);
      applyToken(data.access_token);
    }
    if (data.refresh_token) {
      setStoredRefresh(data.refresh_token);
    }
    if (data.kitchens && data.kitchens.length > 0) {
      localStorage.setItem("manna_kitchens_cache", JSON.stringify(data.kitchens));
    }
    const userState = { ...data };
    delete userState.access_token;
    delete userState.refresh_token;
    setUser(userState);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {});
    } catch (e) {
      // Logout API failure is non-fatal — proceed with local cleanup
    }
    setStoredToken(null);
    setStoredRefresh(null);
    applyToken(null);
    setUser(null);
  };

  const refreshUser = () => fetchMe();

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, setUser, setSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthContext;
